import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mic, Square, Camera, Paperclip, CheckCircle, AlertCircle, ChevronDown, Search, Building2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SEGMENT_MAX_SEC = 25 * 60; // 25 minutes per segment

function fmtTime(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Searchable school dropdown
function SchoolDropdown({ value, onChange, schools }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = schools.filter((s) =>
    s.name.toLowerCase().includes(query.toLowerCase())
  );

  const select = (name) => {
    onChange(name);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="school-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-sm transition-colors ${
          open ? "border-[#E8542A] ring-1 ring-[#E8542A]/20" : "border-[#E8D5C8] hover:border-[#E8542A]/50"
        } bg-white`}
      >
        <span className={value ? "text-[#1A1918]" : "text-[#A8968E]"}>
          {value || "Select or type school name…"}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#8A6D5C] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-2xl border border-black/8 bg-white shadow-xl"
          >
            {/* Search input */}
            <div className="flex items-center gap-2 border-b border-[#F3EFEA] px-3 py-2.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-[#8A6D5C]" />
              <input
                autoFocus
                data-testid="school-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (filtered.length > 0) select(filtered[0].name);
                    else if (query.trim()) select(query.trim());
                  }
                }}
                placeholder="Search schools…"
                className="w-full bg-transparent text-sm text-[#1A1918] outline-none placeholder:text-[#A8968E]"
              />
            </div>

            {/* Options */}
            <div className="max-h-48 overflow-y-auto py-1.5">
              {filtered.length === 0 && query.trim() && (
                <button
                  type="button"
                  onClick={() => select(query.trim())}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#E8542A] hover:bg-[#E8542A]/5"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  Use "<strong>{query.trim()}</strong>"
                </button>
              )}
              {filtered.length === 0 && !query.trim() && (
                <p className="px-4 py-3 text-xs text-[#8A6D5C]">No schools found. Ask your admin to add schools.</p>
              )}
              {filtered.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`school-option-${s.id}`}
                  onClick={() => select(s.name)}
                  className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors hover:bg-[#E8542A]/5 ${
                    value === s.name ? "bg-[#E8542A]/8 font-semibold text-[#E8542A]" : "text-[#1A1918]"
                  }`}
                >
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-[#8A6D5C]" />
                  {s.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function NewSession() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState({ school_name: "", standard: "", division: "", subject: "" });

  // Recording state
  const [recording, setRecording] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [segmentLabel, setSegmentLabel] = useState(""); // e.g. "Part 2"
  const [segments, setSegments] = useState([]); // finalized Blobs
  const [photos, setPhotos] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  // Refs (mutable, avoid stale closures)
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const totalSecondsRef = useRef(0);
  const segmentStartRef = useRef(0);
  const segmentsRef = useRef([]); // synced with state

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      return api.get("/schools");
    }).then((r) => setSchools(r.data || [])).catch(() => navigate("/login"));
    return () => clearInterval(timerRef.current);
  }, [navigate]);

  const beginSegment = (stream) => {
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    const recorder = new MediaRecorder(stream, { mimeType });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      segmentsRef.current = [...segmentsRef.current, blob];
      setSegments([...segmentsRef.current]);
    };
    recorder.start(500); // collect data every 500ms
    recorderRef.current = recorder;
    segmentStartRef.current = totalSecondsRef.current;
  };

  const splitSegment = () => {
    const doneCount = segmentsRef.current.length + 1;
    recorderRef.current?.stop();
    // Start new segment after a brief pause (lets onstop fire)
    setTimeout(() => {
      if (streamRef.current) {
        beginSegment(streamRef.current);
        setSegmentLabel(`Part ${doneCount + 1}`);
        toast.info(`Part ${doneCount} saved — recording continues`, { duration: 4000 });
      }
    }, 120);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      totalSecondsRef.current = 0;
      segmentsRef.current = [];
      setTotalSeconds(0);
      setSegments([]);
      setSegmentLabel("Part 1");
      beginSegment(stream);
      setRecording(true);
      timerRef.current = setInterval(() => {
        totalSecondsRef.current += 1;
        setTotalSeconds((t) => t + 1);
        const elapsed = totalSecondsRef.current - segmentStartRef.current;
        if (elapsed >= SEGMENT_MAX_SEC) {
          splitSegment();
        }
      }, 1000);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRecording(false);
    setSegmentLabel("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (segments.length === 0) {
      toast.error("No recording found. Please record your class first.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      segments.forEach((blob, i) => fd.append("audio_parts", blob, `part_${i + 1}.webm`));
      photos.forEach((p) => fd.append("photos", p));
      attachments.forEach((a) => fd.append("attachments", a));
      const { data } = await api.post("/sessions", fd);
      toast.success("Session saved");
      navigate(`/sessions/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const segCount = segments.length;
  const hasRecording = segCount > 0;
  const totalMin = Math.round(totalSeconds / 60);

  return (
    <main data-testid="new-session-page" className="min-h-screen bg-[#FDF3EC]">
      <TopBar user={user} />
      <div className="mx-auto w-full max-w-2xl px-5 py-10 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 data-testid="new-session-title" className="font-display text-3xl font-bold text-[#1A1918] md:text-4xl">
            New class session
          </h1>
          <p className="mt-1 text-sm text-[#8A6D5C]">Record your class and let AI generate the parent update</p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={submit}
          className="mt-8 space-y-6 rounded-3xl border border-black/5 bg-white p-6 shadow-sm md:p-8"
        >
          {/* Class details */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">School name</Label>
              <div className="mt-1.5">
                <SchoolDropdown
                  value={form.school_name}
                  onChange={(v) => setForm({ ...form, school_name: v })}
                  schools={schools}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="standard" className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Standard</Label>
              <Input
                id="standard"
                data-testid="standard-input"
                placeholder="e.g. 7th"
                className="mt-1.5 rounded-xl border-[#E8D5C8] focus:border-[#E8542A]"
                value={form.standard}
                onChange={(e) => setForm({ ...form, standard: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="division" className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Division</Label>
              <Input
                id="division"
                data-testid="division-input"
                placeholder="e.g. B"
                className="mt-1.5 rounded-xl border-[#E8D5C8] focus:border-[#E8542A]"
                value={form.division}
                onChange={(e) => setForm({ ...form, division: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="subject" className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Subject</Label>
              <Input
                id="subject"
                data-testid="subject-input"
                placeholder="e.g. Mathematics"
                className="mt-1.5 rounded-xl border-[#E8D5C8] focus:border-[#E8542A]"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
              />
            </div>
          </div>

          {/* Recording section */}
          <div className="rounded-2xl bg-[#FDF3EC] p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-[#1A1918]">Class recording</p>
              {segCount > 1 && (
                <span className="rounded-full bg-[#E8542A]/10 px-2.5 py-0.5 text-xs font-semibold text-[#E8542A]">
                  {segCount} parts
                </span>
              )}
            </div>

            {/* Controls */}
            <div className="mt-3 flex items-center gap-4">
              {!recording ? (
                <Button
                  type="button"
                  data-testid="record-start-btn"
                  onClick={startRecording}
                  className="rounded-2xl bg-[#E8542A] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#C94520]"
                >
                  <Mic className="mr-2 h-4 w-4" />
                  {hasRecording ? "Re-record" : "Start recording"}
                </Button>
              ) : (
                <Button
                  type="button"
                  data-testid="record-stop-btn"
                  onClick={stopRecording}
                  className="rounded-2xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700"
                >
                  <Square className="mr-2 h-4 w-4" />
                  End recording
                </Button>
              )}

              <div className="flex flex-col">
                <span data-testid="record-timer" className="font-mono text-sm font-bold text-[#1A1918]">
                  {(recording || hasRecording) ? fmtTime(totalSeconds) : "—"}
                </span>
                {recording && segmentLabel && (
                  <span className="text-xs text-[#E8542A]">{segmentLabel}</span>
                )}
              </div>
            </div>

            {/* Live waveform indicator */}
            {recording && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 flex items-center gap-2"
              >
                <div className="relative flex h-2 w-2">
                  <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </div>
                <span className="text-xs text-[#8A6D5C]">
                  Recording{segCount > 0 ? ` · ${segCount} part${segCount > 1 ? "s" : ""} saved` : ""}
                  {totalSeconds >= SEGMENT_MAX_SEC - 120 && totalSeconds < SEGMENT_MAX_SEC
                    ? " · auto-split in 2 min"
                    : ""}
                </span>
              </motion.div>
            )}

            {/* Segment info after stop */}
            {!recording && hasRecording && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 flex items-center gap-2 text-sm text-emerald-700"
              >
                <CheckCircle className="h-4 w-4" />
                <span>
                  {segCount === 1
                    ? `Recording ready — ${fmtTime(totalSeconds)}`
                    : `${segCount} parts ready — ${fmtTime(totalSeconds)} total`}
                </span>
              </motion.div>
            )}

            {/* Long class notice */}
            {totalSeconds > 0 && totalSeconds < 60 && !hasRecording && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-[#8A6D5C]">
                <AlertCircle className="h-3.5 w-3.5" />
                Classes over 25 min are auto-split into segments — nothing is lost
              </p>
            )}
          </div>

          {/* Attachments */}
          <div className="grid gap-3 sm:grid-cols-2">
            <label
              data-testid="photo-capture-label"
              className="flex cursor-pointer items-center gap-2.5 rounded-2xl border border-dashed border-[#E8542A]/30 p-4 text-sm text-[#8A6D5C] transition-colors hover:border-[#E8542A]/60 hover:bg-[#E8542A]/5"
            >
              <Camera className="h-4 w-4 text-[#E8542A]" />
              {photos.length ? `${photos.length} photo(s) added` : "Capture board / class photos"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                data-testid="photo-capture-input"
                onChange={(e) => setPhotos(Array.from(e.target.files || []))}
              />
            </label>
            <label
              data-testid="attachment-label"
              className="flex cursor-pointer items-center gap-2.5 rounded-2xl border border-dashed border-[#E8542A]/30 p-4 text-sm text-[#8A6D5C] transition-colors hover:border-[#E8542A]/60 hover:bg-[#E8542A]/5"
            >
              <Paperclip className="h-4 w-4 text-[#E8542A]" />
              {attachments.length ? `${attachments.length} file(s) attached` : "Attach worksheets / files"}
              <input
                type="file"
                multiple
                className="hidden"
                data-testid="attachment-input"
                onChange={(e) => setAttachments(Array.from(e.target.files || []))}
              />
            </label>
          </div>

          <Button
            type="submit"
            data-testid="save-session-btn"
            disabled={saving || !hasRecording}
            className="w-full rounded-2xl bg-[#E8542A] py-5 text-sm font-bold text-white hover:bg-[#C94520] disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save session"}
          </Button>
        </motion.form>
      </div>
    </main>
  );
}
